import React, { useMemo, } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  borderRadius,
  colors,
  fontSize,
  fontWeight,
  gaps,
  padding,
} from "theme/defaultStyle";
import ActionButton from "controls/global/action-button/ActionButton";
import { PolicyManagementRecord } from "entities/UIModel/PolicyManagementRecord";
import { PolicyClosePreviewSummaryDto } from "utils/api/policyTransactionApi";

const DialogHeading = styled(DialogTitle)({
  display: "flex",
  alignItems: "center",
  gap: gaps.small1,
  fontSize: fontSize.xlarge,
  fontWeight: fontWeight.bold2,
  color: colors.blue15,
  paddingBottom: gaps.xsmall2,
});

const Subtitle = styled(Typography)({
  fontSize: fontSize.xsmall,
  color: colors.grey11,
  paddingLeft: padding.large1,
  paddingRight: padding.large1,
  marginTop: "-4px",
  marginBottom: gaps.small1,
});

const SectionLabel = styled(Typography)({
  fontSize: fontSize.xxsmall,
  fontWeight: fontWeight.bold2,
  color: colors.blue01,
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  marginBottom: gaps.small1,
});

const SummaryGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: gaps.small1,
  marginBottom: gaps.large1,
});

const SummaryCard = styled(Box)({
  padding: `${padding.small1} ${padding.medium1}`,
  border: `1px solid ${colors.grey09}`,
  borderRadius: borderRadius.small,
  backgroundColor: colors.grey25,
});

const CardLabel = styled(Typography)({
  fontSize: fontSize.xxsmall,
  fontWeight: fontWeight.bold2,
  color: colors.grey11,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
});

const CardValue = styled(Typography)({
  fontSize: fontSize.large,
  fontWeight: fontWeight.bold2,
  color: colors.blue15,
});

const InfoBanner = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  gap: gaps.small1,
  marginTop: gaps.large1,
  padding: `${padding.small1} ${padding.medium1}`,
  backgroundColor: colors.blue08,
  border: `1px solid ${colors.blue07}`,
  borderRadius: borderRadius.small,
  color: colors.blue02,
  fontSize: fontSize.small,
});
/** 
const ConfirmStrip = styled(Box)({
  marginTop: gaps.medium1,
  padding: `${padding.small1} ${padding.medium1}`,
  backgroundColor: "#fff8e5",
  border: "1px solid #e8c068",
  borderRadius: borderRadius.small,
});

const ConfirmPrompt = styled(Typography)({
  fontSize: fontSize.small,
  color: "#7a5a00",
  marginBottom: gaps.xsmall2,
});
*/
interface StateRow {
  state: string;
  products: number;
  liability: number;
  premium: number;
  retention: number;
}

interface ConfirmCloseModalProps {
  open: boolean;
  onClose: () => void;
  records: PolicyManagementRecord[];
  /** Accounting period in MM/YYYY format the user must type to confirm. */
  accountingPeriod: string;
  requireAccountingPeriod?: boolean;
  onConfirm?: () => void;
  onExportPreview?: () => void;
  /** Fresh, server-computed batch summary (Products/Liability/etc). While
   *  null and summaryLoading is false, no fetch has completed yet. */
  summary?: PolicyClosePreviewSummaryDto | null;
  summaryLoading?: boolean;
  summaryError?: boolean;
}

function formatCurrency(value: number): string {
  return (
    "$" +
    value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

// Booked-value resolution mirrors PolicyStatsBar: prefer the reported figure
// when the row is booked as "reported", otherwise fall back to actuals.
function liabilityOf(r: PolicyManagementRecord): number {
  if (r.bookedRow === "reported" && r.reportedLiability !== undefined)
    return r.reportedLiability;
  return r.LiabilityAmount || 0;
}

function premiumOf(r: PolicyManagementRecord): number {
  if (r.bookedRow === "reported" && r.reportedPremium !== undefined)
    return r.reportedPremium;
  return r.ActualFee || 0;
}

function retentionOf(r: PolicyManagementRecord): number {
  if (r.bookedRow === "reported" && r.reportedRetention !== undefined)
    return r.reportedRetention;
  return r.ActualRetention || 0;
}

const ConfirmCloseModal = ({
  open,
  onClose,
  records,
  accountingPeriod,
  requireAccountingPeriod = true,
  onConfirm,
  onExportPreview,
  summary,
  summaryLoading = false,
  summaryError = false,
}: ConfirmCloseModalProps) => {
  /**const [periodInput, setPeriodInput] = useState("");

  useEffect(() => {
    if (open) setPeriodInput("");
  }, [open]);*/

  const totals = useMemo(() => {
    if (summary) {
      return {
        products: summary.TotalProducts ?? 0,
        si: summary.TotalSimultaneous ?? 0,
        liability: summary.TotalLiabilityAmount ?? 0,
        premium: summary.TotalActualFee ?? 0,
        retention: summary.TotalActualRemittance ?? 0,
        states: summary.TotalStates ?? 0,
      };
    }
    const products = records.length;
    const si = records.filter((r) => r.IsSimultaneous).length;
    const liability = records.reduce((s, r) => s + liabilityOf(r), 0);
    const premium = records.reduce((s, r) => s + premiumOf(r), 0);
    const retention = records.reduce((s, r) => s + retentionOf(r), 0);
    const states = new Set(records.map((r) => r.StateCode).filter(Boolean)).size;
    return { products, si, liability, premium, retention, states };
  }, [summary, records]);

  const byState = useMemo<StateRow[]>(() => {
    if (summary) {
      return [...summary.StateSummary]
        .map((r) => ({
          state: r.StateCode || "—",
          products: r.ProductCount ?? 0,
          liability: r.LiabilityAmount ?? 0,
          premium: r.ActualFee ?? 0,
          retention: r.ActualRemittance ?? 0,
        }))
        .sort((a, b) => a.state.localeCompare(b.state));
    }
    const map = new Map<string, StateRow>();
    records.forEach((r) => {
      const state = r.StateCode || "—";
      const existing =
        map.get(state) ||
        ({ state, products: 0, liability: 0, premium: 0, retention: 0 } as StateRow);
      existing.products += 1;
      existing.liability += liabilityOf(r);
      existing.premium += premiumOf(r);
      existing.retention += retentionOf(r);
      map.set(state, existing);
    });
    return [...map.values()].sort((a, b) => a.state.localeCompare(b.state));
  }, [summary, records]);

  const recordCount = records.length;
  const previewReady = !summaryLoading && !summaryError;
  const isConfirmed = previewReady

  const handleConfirm = () => {
    if (!isConfirmed) return;
    onConfirm?.();
    onClose();
  };

  const summaryCards = [
    { label: "Products", value: totals.products.toLocaleString() },
    { label: "SI Transactions", value: totals.si.toLocaleString() },
    { label: "Total Liability", value: formatCurrency(totals.liability) },
    { label: "Policy Premium", value: formatCurrency(totals.premium) },
    { label: "STGC Retention", value: formatCurrency(totals.retention) },
    { label: "States", value: totals.states.toLocaleString() },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogHeading>
        <VerifiedOutlinedIcon sx={{ color: colors.blue01 }} />
        <span>Confirm Close</span>
        <IconButton
          aria-label="Close dialog"
          onClick={onClose}
          size="small"
          sx={{ marginLeft: "auto", color: colors.grey11 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogHeading>
      <Subtitle>
        <strong>{recordCount.toLocaleString()}</strong> record
        {recordCount === 1 ? "" : "s"} will be booked. This action cannot be
        undone.
      </Subtitle>
      <DialogContent>
        {summaryLoading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: gaps.small1, marginBottom: gaps.large1 }}>
            <CircularProgress size={18} />
            <Typography sx={{ fontSize: fontSize.small, color: colors.grey11 }}>
              Loading current batch totals…
            </Typography>
          </Box>
        )}
        {summaryError && (
          <InfoBanner sx={{ backgroundColor: "#fdecea", borderColor: "#f5c2c0", color: "#7a1f1a" }}>
            <InfoOutlinedIcon sx={{ fontSize: 18, flexShrink: 0, marginTop: "1px" }} />
            <Box component="span">
              Couldn&apos;t load the current batch totals. Close and reopen to retry before confirming.
            </Box>
          </InfoBanner>
        )}
        <SectionLabel>Batch Summary</SectionLabel>
        <SummaryGrid>
          {summaryCards.map((c) => (
            <SummaryCard key={c.label}>
              <CardLabel>{c.label}</CardLabel>
              <CardValue>{c.value}</CardValue>
            </SummaryCard>
          ))}
        </SummaryGrid>

        <SectionLabel>By State</SectionLabel>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>STATE</TableCell>
              <TableCell align="right">PRODUCTS</TableCell>
              <TableCell align="right">LIABILITY</TableCell>
              <TableCell align="right">PREMIUM/FEE</TableCell>
              <TableCell align="right">STGC RETENTION</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {byState.map((row) => (
              <TableRow key={row.state}>
                <TableCell sx={{ fontWeight: fontWeight.bold1 }}>
                  {row.state}
                </TableCell>
                <TableCell align="right">{row.products}</TableCell>
                <TableCell align="right">{formatCurrency(row.liability)}</TableCell>
                <TableCell align="right">{formatCurrency(row.premium)}</TableCell>
                <TableCell align="right">{formatCurrency(row.retention)}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ fontWeight: fontWeight.bold2 }}>Total</TableCell>
              <TableCell align="right" sx={{ fontWeight: fontWeight.bold2 }}>
                {totals.products}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: fontWeight.bold2 }}>
                {formatCurrency(totals.liability)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: fontWeight.bold2 }}>
                {formatCurrency(totals.premium)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: fontWeight.bold2 }}>
                {formatCurrency(totals.retention)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <InfoBanner>
          <InfoOutlinedIcon sx={{ fontSize: 18, flexShrink: 0, marginTop: "1px" }} />
          <Box component="span">
            On confirmation: <strong>IsClosed</strong> set,{" "}
            <strong>ClosedDate</strong> and <strong>ClosedByUserID</strong>{" "}
            stamped, records move to <strong>History</strong>, and{" "}
            <strong>PremiumReceivables</strong> records are auto-generated from
            ActualRetention.
          </Box>
        </InfoBanner>
        
        {/**requireAccountingPeriod && (
          <ConfirmStrip>
            <ConfirmPrompt>
              Type the accounting period <strong>{accountingPeriod}</strong> to
              confirm closing all records.
            </ConfirmPrompt>
            <TextField
              size="small"
              placeholder="MM/YYYY"
              value={periodInput}
              onChange={(e) => setPeriodInput(e.target.value)}
              sx={{ width: 160, backgroundColor: colors.white }}
            />
          </ConfirmStrip>
        )*/}
      </DialogContent>
      <DialogActions>
        <ActionButton variant="outlined" color="secondary" onClick={onClose}>
          Cancel
        </ActionButton>
        <ActionButton
          variant="outlined"
          color="primary"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={onExportPreview}
        >
          Export Preview Report
        </ActionButton>
        <ActionButton
          variant="contained"
          color="primary"
          startIcon={<VerifiedOutlinedIcon />}
          disabled={!isConfirmed}
          onClick={handleConfirm}
        >
          Confirm Close - {recordCount} Record{recordCount === 1 ? "" : "s"}
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmCloseModal;
